import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  ArrowLeft,
  Clock,
  Loader2,
  RefreshCw,
  ListTodo,
  CheckCircle,
  XCircle,
  Hourglass,
  Server,
} from 'lucide-react';
import { fetchSharedServerQueue, fetchSharedServerStats } from '@/api/admin';

export function SharedServerQueuePage() {
  const [autoRefresh, setAutoRefresh] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-shared-servers-queue'],
    queryFn: fetchSharedServerQueue,
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const { data: statsData } = useQuery({
    queryKey: ['admin-shared-servers-stats'],
    queryFn: fetchSharedServerStats,
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const calculateWaitTime = (queuedAt: string | null | undefined) => {
    if (!queuedAt) return '—';
    const queued = new Date(queuedAt);
    const now = new Date();
    const diffMs = now.getTime() - queued.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ${diffHours % 24}h`;
  };

  const getStatusBadge = (status: string) => {
    const lower = status?.toLowerCase() || '';
    if (lower === 'completed' || lower === 'success' || lower === 'assigned') {
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <CheckCircle className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
    if (lower.includes('error') || lower === 'failed') {
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
    if (lower === 'pending' || lower === 'queued' || lower === 'waiting') {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Hourglass className="h-3 w-3 mr-1" />
          {status}
        </Badge>
      );
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const queueItems = data?.queue_items || [];
  const pendingCount = queueItems.filter(
    (item) => item.status?.toLowerCase() === 'pending' || item.status?.toLowerCase() === 'queued'
  ).length;
  const assignedCount = queueItems.filter(
    (item) => item.status?.toLowerCase() === 'assigned'
  ).length;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/shared-servers">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Servers
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">Build Queue</h1>
            <p className="text-sm text-muted-foreground">Current queue across all shared servers</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ListTodo className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total in Queue</p>
                <p className="text-2xl font-semibold">{queueItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Hourglass className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-semibold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Assigned</p>
                <p className="text-2xl font-semibold">{assignedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Server className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Available Servers</p>
                <p className="text-2xl font-semibold">{statsData?.summary?.available_servers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load queue.</AlertDescription>
        </Alert>
      )}

      {/* Queue Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5" />
            Queue Items ({queueItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : queueItems.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-3 px-2 font-medium">ID</th>
                    <th className="py-3 px-2 font-medium">Build</th>
                    <th className="py-3 px-2 font-medium">Status</th>
                    <th className="py-3 px-2 font-medium">Wait Time</th>
                    <th className="py-3 px-2 font-medium">Queued At</th>
                    <th className="py-3 px-2 font-medium">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {queueItems.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <code className="text-xs">#{item.id}</code>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-medium">Build #{item.build_id}</span>
                      </td>
                      <td className="py-3 px-2">
                        {getStatusBadge(item.status)}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>{calculateWaitTime(item.queued_at)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-xs text-muted-foreground">
                        {item.queued_at ? new Date(item.queued_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant="outline">
                          {(item as any).priority || 'Normal'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <ListTodo className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Queue is empty</p>
              <p className="text-sm mt-2">
                Builds will appear here when they're waiting for a shared server
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Server Stats */}
      {statsData && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Server Availability</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Total Servers</p>
                <p className="text-lg font-semibold">{statsData.summary.total_servers}</p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Available</p>
                <p className="text-lg font-semibold">{statsData.summary.available_servers}</p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Active Builds</p>
                <p className="text-lg font-semibold">{statsData.summary.total_active_builds}</p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Builds Queued</p>
                <p className="text-lg font-semibold">{statsData.summary.builds_queued}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
